import React, { useState, useMemo, useRef } from 'react';
import Appearance from 'styles/Appearance.js';
import Utils from 'files/Utils.js';
import Analytics from 'files/Analytics.js';
import Button from 'views/Button.js';
import { leakSources, bathroomFixtures, packages, starterPack } from 'files/LeakCoverage.js';
import { waterSensorTotal, tiersInEffect } from 'files/SensorPricing.js';
import { applyOffer, offerPercent } from 'files/LimitedOffer.js';
import PriceGate from 'views/PriceGate.js';
import ShopPayInstallments from 'views/ShopPayInstallments.js';
import { useServiceability } from 'views/ServiceabilityProvider.js';

// Guided "Find Your Solution" quiz. All priorities/coverage come from
// LeakCoverage.js; all prices come from the live product data (never hardcoded).
// Sections reveal progressively — nothing is pre-answered.
// `source` ('modal' | 'page') tags the GTM quiz events.
const CoverageQuiz = ({ products, onAddPackage, source }) => {

    const [bathroomCount, setBathroomCount] = useState(null);
    const [quantities, setQuantities] = useState({}); // item id -> count
    const [showResults, setShowResults] = useState(false);
    const started = useRef(false);

    // The quiz counts as started on the first answer, not on open.
    const trackAnswer = (question, answer, quantity) => {
        if(!started.current) {
            started.current = true;
            Analytics.quizStart({ source });
        }
        Analytics.quizAnswer({ source, question, answer, quantity });
    };

    // Set an item's quantity, clamped at 0.
    const setQty = (item, n) => {
        const qty = Math.max(0, n);
        setQuantities(q => ({ ...q, [item.id]: qty }));
        const question = bathroomFixtures.includes(item) ? 'bathroom_fixtures' : 'other_leak_sources';
        trackAnswer(question, item.id, qty);
    };

    // Picking a bathroom count seeds one sink + one toilet sensor per bathroom
    // (the customer can adjust those quantities afterward).
    const selectBathrooms = (n) => {
        setBathroomCount(n);
        setQuantities(q => ({ ...q, 'bath-sink': n, 'toilet': n }));
        trackAnswer('bathrooms', n === 4 ? '4+' : `${n}`, n);
    };

    const revealResults = () => {
        setShowResults(true);
        Analytics.quizComplete({
            source,
            bathrooms: bathroomCount === 4 ? '4+' : `${bathroomCount}`,
            totalAreas,
            answers: Object.fromEntries(Object.entries(quantities).filter(([, qty]) => qty > 0)),
            recommendedPackage: packages.find(pkg => pkg.recommended)?.key,
        });
    };

    // Prices from product data — Shopify is the source of truth.
    const starterProduct = (products || []).find(p => p.handle === starterPack.handle);
    const sensorProduct = (products || []).find(p => p.handle === starterPack.sensorHandle);
    const starterPrice = parseFloat(starterProduct?.price) || 0;
    const starterCompare = parseFloat(starterProduct?.compareAtPrice) || 0;
    const sensorPrice = parseFloat(sensorProduct?.price) || 0;

    // Each unit of each selected item becomes an "area" with a tier.
    const areas = useMemo(() => {
        const list = [];
        [...leakSources, ...bathroomFixtures].forEach(item => {
            const qty = quantities[item.id] || 0;
            for(let i = 0; i < qty; i++) list.push(item.tier);
        });
        return list;
    }, [quantities]);

    const totalAreas = areas.length;

    // Limited-time offer percents (0 unless an offer is live). The sensor
    // percent comes off the tier total, matching the Shopify Function.
    const { offer, volumePricing } = useServiceability();

    // À-la-carte sensor tiers, only where checkout charges them (see tiersInEffect).
    const sensorTiers = tiersInEffect(sensorProduct, { volumePricing, offer });
    const starterOffer = offerPercent(starterProduct, offer);
    const sensorOffer = offerPercent(sensorProduct, offer);

    const tierData = packages.map(pkg => {
        const covered = areas.filter(t => pkg.tiers.includes(t)).length;
        const sensors = Math.max(covered, starterPack.sensorsIncluded);
        const extra = sensors - starterPack.sensorsIncluded;
        // Extra sensors beyond the Base Network get the same volume pricing.
        const extraFull = extra * sensorPrice;
        const extraNet = sensorTiers ? waterSensorTotal(sensorTiers, extra) : extraFull;
        const baseCompare = starterCompare > 0 ? starterCompare : starterPrice;
        const offered = (starterOffer > 0) || (extra > 0 && sensorOffer > 0);
        return {
            ...pkg, sensors, extra,
            price: applyOffer(starterPrice, starterOffer) + (extra > 0 ? applyOffer(extraNet, sensorOffer) : 0),
            // With an offer running, always strike through the pre-offer price.
            compareAt: (extra > 0 || offered) ? baseCompare + extraFull : (starterCompare > 0 ? starterCompare : 0),
            // Amount saved purely from volume pricing on the extra sensors; the
            // "Save" label only shows while the volume discount itself is live.
            volumeSavings: volumePricing ? Math.max(0, Math.round((extraFull - extraNet) * 100) / 100) : 0,
        };
    });

    // --- UI helpers -------------------------------------------------------
    const chip = (label, active, onClick, icon) => (
        <div
            onClick={onClick}
            style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 8,
                padding: '10px 16px',
                borderRadius: 999,
                cursor: 'pointer',
                border: `1px solid ${active ? Appearance.colors.primary() : Appearance.colors.border()}`,
                backgroundColor: active ? Utils.hexToRGBA(Appearance.colors.primary(), 0.08) : Appearance.colors.white,
                color: active ? Appearance.colors.primary() : Appearance.colors.text(),
                fontFamily: Appearance.fonts.body,
                fontSize: 14,
                fontWeight: 500,
                transition: 'all 0.15s ease',
                userSelect: 'none',
            }}>
            {icon && <i className={icon} style={{ fontSize: 13, opacity: active ? 1 : 0.6 }} />}
            {label}
        </div>
    );

    const questionLabel = (text) => (
        <div style={{ ...Appearance.textStyles.subHeading({ fontSize: 17 }), marginBottom: 14 }}>{text}</div>
    );

    // A tappable quantity chip: tap to add (0 → 1), then adjust with − / +.
    const qtyChip = (item) => {
        const qty = quantities[item.id] || 0;
        const active = qty > 0;
        const step = (icon, onClick) => (
            <button
                onClick={(e) => { e.stopPropagation(); onClick(); }}
                aria-label={icon.includes('minus') ? 'Decrease' : 'Increase'}
                style={{
                    width: 24, height: 24, borderRadius: 999, border: 'none', cursor: 'pointer',
                    backgroundColor: Utils.hexToRGBA(Appearance.colors.primary(), 0.14),
                    color: Appearance.colors.primary(),
                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 11,
                }}>
                <i className={icon} />
            </button>
        );
        return (
            <div
                key={item.id}
                onClick={active ? undefined : () => setQty(item, 1)}
                style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 8,
                    padding: active ? '6px 8px 6px 14px' : '10px 16px',
                    borderRadius: 999,
                    cursor: active ? 'default' : 'pointer',
                    border: `1px solid ${active ? Appearance.colors.primary() : Appearance.colors.border()}`,
                    backgroundColor: active ? Utils.hexToRGBA(Appearance.colors.primary(), 0.08) : Appearance.colors.white,
                    color: active ? Appearance.colors.primary() : Appearance.colors.text(),
                    fontFamily: Appearance.fonts.body,
                    fontSize: 14,
                    fontWeight: 500,
                    transition: 'all 0.15s ease',
                    userSelect: 'none',
                }}>
                {item.icon && <i className={item.icon} style={{ fontSize: 13, opacity: active ? 1 : 0.6 }} />}
                <span>{item.label}</span>
                {active && (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, marginLeft: 4 }}>
                        {step('fas fa-minus', () => setQty(item, qty - 1))}
                        <span style={{ minWidth: 12, textAlign: 'center', fontWeight: 700 }}>{qty}</span>
                        {step('fas fa-plus', () => setQty(item, qty + 1))}
                    </span>
                )}
            </div>
        );
    };

    return (
        <div>
            {/* Q1 — bathrooms */}
            <div>
                {questionLabel('How many bathrooms are in your home?')}
                <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                    {[1, 2, 3, 4].map(n => chip(n === 4 ? '4+' : `${n}`, bathroomCount === n, () => selectBathrooms(n)))}
                </div>
            </div>

            {/* Q2 — bathroom fixtures (seeded) + everything else, with quantities */}
            {bathroomCount !== null && (
                <div style={{ marginTop: 36 }}>
                    {questionLabel('Your bathrooms')}
                    <p style={{ ...Appearance.textStyles.caption({ color: Appearance.colors.subText() }), marginTop: -6, marginBottom: 14 }}>
                        {'Each bathroom includes a sink and toilet — adjust if yours differ.'}
                    </p>
                    <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                        {bathroomFixtures.map(qtyChip)}
                    </div>

                    <div style={{ marginTop: 30 }}>
                        {questionLabel('What else is in your home?')}
                        <p style={{ ...Appearance.textStyles.caption({ color: Appearance.colors.subText() }), marginTop: -6, marginBottom: 14 }}>
                            {'Tap to add — bump the count if you have more than one.'}
                        </p>
                        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                            {leakSources.map(qtyChip)}
                        </div>
                    </div>
                </div>
            )}

            {/* Reveal button (after bathrooms answered, before results) */}
            {bathroomCount !== null && !showResults && (
                <div style={{ marginTop: 32 }}>
                    <Button label={'See my recommendation ›'} type={'primary'} onClick={revealResults} style={{ padding: '14px 32px' }} />
                </div>
            )}

            {/* Results (after reveal) */}
            {showResults && (
                <div style={{ marginTop: 40 }}>
                    <div style={{ textAlign: 'center', marginBottom: Utils.isMobile() ? 24 : 32 }}>
                        <span style={Appearance.textStyles.body({ color: Appearance.colors.subText() })}>
                            {'We found about '}
                            <b style={{ color: Appearance.colors.text() }}>{`${totalAreas} place${totalAreas === 1 ? '' : 's'}`}</b>
                            {' water could leak. Here’s your coverage:'}
                        </span>
                    </div>

                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: Utils.isMobile() ? '1fr' : 'repeat(3, 1fr)',
                        gap: Utils.isMobile() ? 16 : 20,
                    }}>
                        {tierData.map(pkg => {
                            const isRec = pkg.recommended;
                            return (
                                <div key={pkg.key} style={{
                                    ...Appearance.styles.card(),
                                    overflow: 'visible',
                                    boxShadow: isRec ? Appearance.boxShadow.cardHover : Appearance.boxShadow.card,
                                    border: `${isRec ? 2 : 1}px solid ${isRec ? Appearance.colors.primary() : Appearance.colors.border()}`,
                                    padding: Utils.isMobile() ? 24 : 26,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    position: 'relative',
                                }}>
                                    {isRec && (
                                        <span style={{
                                            position: 'absolute', top: -11, left: 24,
                                            backgroundColor: Appearance.colors.primary(),
                                            color: Appearance.colors.white,
                                            fontFamily: Appearance.fonts.body, fontSize: 11, fontWeight: 700,
                                            letterSpacing: '0.06em', textTransform: 'uppercase',
                                            padding: '4px 12px', borderRadius: 999,
                                        }}>
                                            {'Recommended'}
                                        </span>
                                    )}

                                    <div style={{ ...Appearance.textStyles.label({ color: isRec ? Appearance.colors.primary() : Appearance.colors.subText() }), marginBottom: 8 }}>
                                        {pkg.label}
                                    </div>
                                    <div style={{ ...Appearance.textStyles.subHeading({ fontSize: 17 }), marginBottom: 16 }}>
                                        {`Base Network + ${3 + pkg.extra} sensors`}
                                    </div>
                                    <p style={{ ...Appearance.textStyles.body({ fontSize: 14 }), marginBottom: 20, flexGrow: 1 }}>
                                        {pkg.note}
                                    </p>

                                    <PriceGate>
                                        {/* reserved slot: keeps the price on the same line across all three cards */}
                                        <div style={{ minHeight: 25, marginBottom: 20, display: 'flex', alignItems: 'center' }}>
                                            {pkg.volumeSavings > 0 && (
                                                <span style={{
                                                    display: 'inline-flex',
                                                    alignItems: 'center',
                                                    gap: 6,
                                                    backgroundColor: Utils.hexToRGBA(Appearance.colors.success, 0.12),
                                                    color: Appearance.colors.success,
                                                    fontFamily: Appearance.fonts.body,
                                                    fontSize: 12,
                                                    fontWeight: 700,
                                                    padding: '3px 9px',
                                                    borderRadius: 999,
                                                    whiteSpace: 'nowrap',
                                                }}>
                                                    <i className={'fas fa-tags'} style={{ fontSize: 10 }} />
                                                    {`Save ${Utils.formatCurrency(pkg.volumeSavings)}`}
                                                </span>
                                            )}
                                        </div>

                                        <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 10 }}>
                                            <span style={Appearance.textStyles.heading({ fontSize: 26 })}>
                                                {Utils.formatCurrency(pkg.price)}
                                            </span>
                                            {pkg.compareAt > pkg.price && (
                                                <span style={{ ...Appearance.textStyles.body({ fontSize: 15 }), textDecoration: 'line-through', color: Appearance.colors.lightGrey }}>
                                                    {Utils.formatCurrency(pkg.compareAt)}
                                                </span>
                                            )}
                                        </div>

                                        <ShopPayInstallments price={pkg.price} style={{ marginBottom: 18 }} />

                                        <Button
                                            label={'Add to Cart'}
                                            type={isRec ? 'primary' : 'outline'}
                                            onClick={() => onAddPackage && onAddPackage(pkg.extra, pkg.key)}
                                            style={{ width: '100%' }} />
                                    </PriceGate>
                                </div>
                            );
                        })}
                    </div>

                    <p style={{ ...Appearance.textStyles.caption({ color: Appearance.colors.lightGrey, textAlign: 'center' }), marginTop: 20 }}>
                        {'Every package includes the Bedside Control Center and Smart Hub. Add more sensors any time.'}
                    </p>
                </div>
            )}
        </div>
    );
};

export default CoverageQuiz;
